import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { absoluteBrandingUrl, useBranding } from '../../../contexts/BrandingContext';
import { aboutLink, dashboardLink, loginLink, registerLink } from '../../../library/constants';
import { HomeContent, clinicHomeContent, platformHomeContent } from '../../../library/models/homeContent';
import { ITenantHome } from '../../../library/models/tenantHome';
import { useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { LandingPageView } from './LandingPageView';

/**
 * Who the front page is talking to, which the hostname decides.
 *
 * On the platform's own address it is a clinic considering Carefull, so the copy sells the
 * product: "Why Carefull", "Everything a clinic runs on", "each clinic's data stays its own".
 * On motioncare.ro it is a PATIENT, and every one of those sentences is wrong there -- some
 * merely irrelevant, some actively alarming. A patient reading that nothing crosses from one
 * clinic to another has just learned their clinic is one tenant of somebody's software, which
 * is exactly what a clinic's own front door should never say.
 *
 * So the clinic variant is a different address to a different reader, not a re-skin.
 *
 * <h2>The hostname decides, and signing in does not change it</h2>
 *
 * The clinic's own copy is fetched through the PUBLIC route, whether or not anybody is signed
 * in. There is an authenticated equivalent and this page deliberately never calls it: `/home`
 * is a shopfront, and a patient who signs in should not watch the front page they were just
 * reading rearrange itself. The authenticated route exists for the console's Home tab, which
 * is where a clinic's administrator writes this copy -- and which is reachable from the
 * platform's address and from localhost, where no hostname names a clinic.
 *
 * <h2>Where the words come from</h2>
 *
 * From the clinic's row, and nowhere else: every clinic's page is stored whole, starting from
 * the starter copy the platform writes when the clinic is created. {@link clinicHomeContent}
 * turns the row into finished strings for {@link LandingPageView}.
 *
 * Which means there is nothing to show before the row arrives. The page used to paper over
 * that with shipped copy, and a clinic with words of its own briefly showed ours; now the hero
 * holds placeholders until it lands. If it never does, the page falls to the clinic's name and
 * its buttons -- see `clinicHomeContent` -- rather than to words the clinic never wrote.
 */
export const LandingPage = (): JSX.Element => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const { i18n, t: translate } = useTranslation();
    const { hostContext, hostname } = useBranding();

    const clinic: string | null = hostContext?.displayName ?? null;

    /**
     * The clinic's own front-page copy, when the hostname names a clinic.
     *
     * `enabled` is what keeps the platform's address from asking: there is no clinic to fetch
     * one for, the route would answer 404, and the platform's page is not a clinic's to
     * configure. It is also why an unregistered hostname costs no round trip.
     */
    const { data, isPending } = useReactQuery<ITenantHome>(
        services.publicTenant.getTenantHome(hostname),
        { enabled: clinic !== null }
    );

    /**
     * Rebuilt whenever the copy, the language or the clinic changes -- which is every input
     * the resolution has. Not memoised: it is a few dozen string comparisons on a page that
     * renders once, and a dependency list this long is more likely to go stale than to pay.
     */
    const content: HomeContent = clinic === null
        ? platformHomeContent(translate)
        : clinicHomeContent(
            data,
            i18n.language,
            clinic,
            absoluteBrandingUrl(data?.heroImageUrl, data?.heroImageEtag)
        );

    return (
        <LandingPageView
            content={content}
            isLoading={clinic !== null && isPending}
            isAuthenticated={isAuthenticated}
            onPrimaryAction={() => navigate(isAuthenticated ? dashboardLink : registerLink)}
            onSignIn={() => navigate(loginLink)}
            onLearnMore={() => navigate(aboutLink)}
        />
    );
};
